import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../core/environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import {
  endpoint,
  MarkAsReadResponse,
  NotificationResponse,
  Notification,
  NotificationData,
} from '../models/overlay-notifications.model';
import { UsersService } from '../../features/users/services/users.service';

@Injectable({
  providedIn: 'root',
})
export class OverlayNotificationsService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;
  private readonly _userId = inject(UsersService).currentUser().id;

  private readonly _loading = signal<boolean>(false);
  public readonly loading = this._loading.asReadonly();
  private readonly _hasUnread = signal<boolean>(false);
  public readonly hasUnread = this._hasUnread.asReadonly();
  private readonly _hasUnreadCount = signal<number>(0);
  public readonly hasUnreadCount = this._hasUnreadCount.asReadonly();
  private readonly _notificationsData = signal<NotificationData | null>(null);
  public readonly notificationsData = this._notificationsData.asReadonly();

  constructor() {
    this.loadHasUnread(this._userId);
  }

  public updateHasUnread(count: number): void {
    this._hasUnreadCount.set(count);
    this._hasUnread.set(count > 0);
  }

  public loadHasUnread(userId: number): void {
    const url = `${this._apiUrl}/${endpoint.loadHasUnread}/${userId}`;
    this._http
      .get<{ data: { hasUnreadNotifications: boolean } }>(url)
      .subscribe((res) => {
        this._hasUnread.set(res.data.hasUnreadNotifications);
      });
  }

  public forceRefresh(userId: number): void {
    this._loading.set(true);
    this.getNotificationsByUserId(userId)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  getNotificationsByUserId(userId: number): Observable<NotificationResponse> {
    const url = `${this._apiUrl}/${endpoint.getNotifications}/${userId}`;

    return this._http.get<NotificationResponse>(url).pipe(
      tap((res) => {
        this._notificationsData.set(res.data);
        this.updateHasUnread(res.data.unreadCount);
        this._loading.set(false);
      }),
    );
  }

  public markNotificationAsRead(
    notificationId: number,
  ): Observable<MarkAsReadResponse> {
    const url = `${this._apiUrl}/${endpoint.markAsReadNotification}/${notificationId}`;
    return this._http.get<MarkAsReadResponse>(url);
  }

  public markAllNotificationsAsRead(
    userId: number,
  ): Observable<{ data: Notification[]; message: string; code: number }> {
    const url = `${this._apiUrl}/${endpoint.markAllAsRead}/${userId}`;
    return this._http.get<{
      data: Notification[];
      message: string;
      code: number;
    }>(url);
  }

  public deleteNotification(notificationId: number): Observable<void> {
    const url = `${this._apiUrl}/${endpoint.getNotifications}/${notificationId}`;
    return this._http.delete<void>(url);
  }
}
